/**
 * HTML 净化工具函数
 *
 * dompurify 作为可选 peerDependency，使用参数注入模式避免硬依赖。
 */

/** DOMPurify sanitize 函数签名 */
type SanitizeFn = (html: string, options?: Record<string, unknown>) => string;

/** 缓存 sanitize 函数引用 */
let cachedSanitize: SanitizeFn | null = null;

/**
 * 初始化 HTML 净化器
 *
 * 在应用启动时调用一次，传入 DOMPurify 实例。
 *
 * @param purify - DOMPurify 实例 (import DOMPurify from 'dompurify')
 *
 * @example
 * ```ts
 * // 在 app.tsx 中初始化
 * import DOMPurify from 'dompurify';
 * import { initSanitizer } from '@aspect/shared/utils';
 * initSanitizer(DOMPurify);
 * ```
 */
export function initSanitizer(purify: { sanitize: SanitizeFn }): void {
  cachedSanitize = purify.sanitize.bind(purify);
}

/**
 * 净化 HTML 字符串，防止 XSS 攻击
 *
 * 需先通过 `initSanitizer` 初始化 DOMPurify。
 *
 * @param html - 待净化的 HTML 字符串
 * @param options - DOMPurify 选项
 * @returns 净化后的安全 HTML 字符串
 *
 * @example
 * ```ts
 * const safeHtml = sanitizeHtml('<script>alert("xss")</script><p>Hello</p>');
 * // '<p>Hello</p>'
 * ```
 */
export function sanitizeHtml(
  html: string,
  options?: Record<string, unknown>,
): string {
  if (!html) return '';

  if (cachedSanitize) {
    return cachedSanitize(html, options);
  }

  throw new Error(
    '[sanitizeHtml] DOMPurify is not initialized. Call initSanitizer(DOMPurify) at app startup.',
  );
}

/**
 * 移除所有 HTML 标签，只保留纯文本
 *
 * @param html - HTML 字符串
 * @returns 纯文本
 */
export function stripHtmlTags(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').trim();
}
